import { useEffect, useState, useCallback } from "react";
import Shell from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Download, Plus, Gauge, Gift, ShoppingCart, RefreshCw, ShieldCheck,
  CreditCard, Building2, Trash2, Star, PiggyBank,
} from "lucide-react";

const money = (m) => `GBP ${(((m || 0) / 100)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
import { PayPalTopUp } from "@/components/PayPalTopUp";
import { PaystackTopUp } from "@/components/PaystackTopUp";
import { MpesaTopUp } from "@/components/MpesaTopUp";
import { useCurrency } from "@/context/CurrencyContext";
import CurrencySelect from "@/components/CurrencySelect";
const COMPANY = "Melian Dialogue Limited (Company Number PVT-27ULY98K) t/a Kadana · info@kadana.app";

export default function Billing() {
  const { org, user } = useAuth();
  const { format: fmtCur, isGbp, active: curActive } = useCurrency();
  const [bal, setBal] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creditPrice, setCreditPrice] = useState(300);

  // Category 6 state
  const [runway, setRunway] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [perApp, setPerApp] = useState([]);
  const [settings, setSettings] = useState(null);
  // draft forms
  const [ar, setAr] = useState({ enabled: false, threshold_credits: 50, topup_credits: 250, monthly_cap_pounds: 100 });
  const [limitPounds, setLimitPounds] = useState("");
  const [inv, setInv] = useState({ company_name: "", vat_number: "", address: "" });
  const [pm, setPm] = useState({ brand: "card", last4: "", exp_month: 12, exp_year: 2030, label: "" });

  const load = useCallback(async () => {
    if (!org) return;
    try {
      const [b, l] = await Promise.all([
        api.get(`/orgs/${org.id}/balance`),
        api.get(`/orgs/${org.id}/ledger`).catch(() => ({ data: [] })),
      ]);
      setBal(b.data.balance_credits);
      setLedger(l.data || []);
    } catch (e) { /* balance still shows */ }
    api.get(`/orgs/${org.id}/runway`).then(r => setRunway(r.data)).catch(() => {});
    api.get(`/orgs/${org.id}/credit-breakdown`).then(r => setBreakdown(r.data)).catch(() => {});
    api.get(`/orgs/${org.id}/billing-settings`).then(r => {
      const s = r.data; setSettings(s);
      setInv(s.invoicing || { company_name: "", vat_number: "", address: "" });
      const a = s.auto_reload || {};
      setAr({
        enabled: !!a.enabled,
        threshold_credits: a.threshold_credits ?? 50,
        topup_credits: a.topup_credits ?? 250,
        monthly_cap_pounds: a.monthly_cap_minor ? a.monthly_cap_minor / 100 : 100,
      });
      setLimitPounds(s.spending_limit_minor != null ? String(s.spending_limit_minor / 100) : "");
    }).catch(() => {});
    api.get("/builder/apps/credit-summary").then(r => setPerApp(r.data.apps || [])).catch(() => {});
  }, [org]);

  useEffect(() => {
    load();
    api.get("/pricing-page?currency=GBP").then(r => setTiers(r.data.top_up_tiers || [])).catch(() => {});
    api.get("/pricing").then(r => setCreditPrice(r.data.credit_list_price_minor || 300)).catch(() => {});
    // eslint-disable-next-line
  }, [org]);

  const topup = async (credits) => {
    if (!org || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${org.id}/topup`, { credits });
      toast.success(data.message || `Added ${credits} credits`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  // ---- Auto-reload (off by default; cannot enable without a real cap) ----
  const saveAutoReload = async (enabled) => {
    if (!org) return;
    const cap_minor = Math.round(Number(ar.monthly_cap_pounds || 0) * 100);
    try {
      const { data } = await api.put(`/orgs/${org.id}/billing-settings/auto-reload`, {
        enabled,
        threshold_credits: Number(ar.threshold_credits) || 0,
        topup_credits: Number(ar.topup_credits) || 0,
        monthly_cap_minor: cap_minor || null,
      });
      setSettings(s => ({ ...s, auto_reload: data.auto_reload }));
      setAr(a => ({ ...a, enabled }));
      toast.success(enabled ? "Auto-reload turned on with your cap." : "Auto-reload turned off.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveLimit = async () => {
    if (!org) return;
    const val = limitPounds === "" ? null : Math.round(Number(limitPounds) * 100);
    try {
      await api.put(`/orgs/${org.id}/billing-settings/spending-limit`, { monthly_cap_minor: val });
      setSettings(s => ({ ...s, spending_limit_minor: val }));
      toast.success(val == null ? "Spending limit cleared." : "Spending limit saved.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveInvoicing = async () => {
    if (!org) return;
    try {
      const { data } = await api.put(`/orgs/${org.id}/billing-settings/invoicing`, inv);
      setSettings(s => ({ ...s, invoicing: data.invoicing }));
      toast.success("Invoicing details saved.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addPm = async () => {
    if (!org) return;
    try {
      const { data } = await api.post(`/orgs/${org.id}/payment-methods`, pm);
      setSettings(s => ({ ...s, payment_methods: data.payment_methods }));
      setPm({ brand: "card", last4: "", exp_month: 12, exp_year: 2030, label: "" });
      toast.success("Payment method added (SANDBOX).");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setDefaultPm = async (id) => {
    try { const { data } = await api.post(`/orgs/${org.id}/payment-methods/${id}/default`);
      setSettings(s => ({ ...s, payment_methods: data.payment_methods })); } catch (e) { toast.error(errMsg(e)); }
  };
  const removePm = async (id) => {
    try { const { data } = await api.delete(`/orgs/${org.id}/payment-methods/${id}`);
      setSettings(s => ({ ...s, payment_methods: data.payment_methods })); } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- Receipts & invoices ----
  const isPurchase = (e) => e.delta_credits > 0 && /topup|purchase|subscription|auto_reload/i.test(e.reason || "");
  const docFor = (e, asInvoice) => {
    const invoicing = settings?.invoicing || {};
    const hasBiz = asInvoice && (invoicing.company_name || invoicing.vat_number);
    const amt = money(Math.abs(e.delta_credits) * creditPrice);
    const rows = [
      [asInvoice ? "Invoice ID" : "Receipt ID", e.id],
      ["Date", new Date(e.created_at).toLocaleString("en-GB")],
      ["Account", `${user?.email || ""} — org ${org?.id || ""}`],
      ...(hasBiz ? [["Billed to", `${invoicing.company_name || ""}${invoicing.address ? ", " + invoicing.address : ""}`]] : []),
      ...(hasBiz && invoicing.vat_number ? [["VAT number", invoicing.vat_number]] : []),
      ["Description", `${(e.reason || "").replace(/_/g, " ")}${e.note ? " — " + e.note : ""}`],
      ["Credits", `${e.delta_credits > 0 ? "+" : ""}${e.delta_credits}`],
      ["Amount (ex-VAT)", amt],
    ];
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Kadana ${asInvoice ? "invoice" : "receipt"} ${e.id}</title>
<style>body{font-family:Inter,system-ui,sans-serif;color:#18181b;max-width:640px;margin:40px auto;padding:0 24px}
h1{font-size:20px}table{width:100%;border-collapse:collapse;margin-top:20px}td{padding:8px 0;border-bottom:1px solid #e4e4e7}
td:first-child{color:#71717a;width:38%}.muted{color:#71717a;font-size:12px}</style></head><body>
<h1>Kadana — ${asInvoice ? "VAT Invoice" : "Credit Receipt"}</h1>
<p class="muted">${COMPANY}</p>
<table>${rows.map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join("")}</table>
<p class="muted" style="margin-top:24px">Credits are a prepaid, non-cash usage unit. Prices shown ex-VAT; VAT is applied
at checkout via Stripe Tax where applicable.${asInvoice && !hasBiz ? " Add your company name and VAT number under Business invoicing details to produce a full business invoice." : ""}</p>
</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const a = document.createElement("a");
    a.href = url; a.download = `kadana-${asInvoice ? "invoice" : "receipt"}-${e.id}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadHistoryCsv = () => {
    const purchases = ledger.filter(isPurchase);
    const head = "date,receipt_id,description,credits,amount_gbp\n";
    const body = purchases.map(e =>
      [new Date(e.created_at).toISOString(), e.id,
       `"${((e.reason || "").replace(/_/g, " ") + (e.note ? " - " + e.note : "")).replace(/"/g, "'")}"`,
       e.delta_credits, (Math.abs(e.delta_credits) * creditPrice / 100).toFixed(2)].join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([head + body], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `kadana-purchase-history-${org?.id || "account"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const low = runway ? runway.balance_credits < (settings ? 100 : 100) : (bal != null && bal < 100);
  const canEnableAr = Number(ar.monthly_cap_pounds) > 0 && Number(ar.threshold_credits) > 0 && Number(ar.topup_credits) > 0;
  const methods = settings?.payment_methods || [];
  const card = { padding: 22, marginTop: 18 };

  return (
    <Shell active="billing">
      <h1 className="k-heading" style={{ fontSize: 30, margin: 0 }} data-testid="billing-title">Billing & credits</h1>
      <div style={{ marginTop: 6, display: "flex", justifyContent: "flex-end" }} data-testid="billing-currency-toggle"><CurrencySelect compact /></div>

      {/* Balance + runway */}
      <div className="k-container" data-testid="billing-balance" style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span className={`k-badge ${low ? "k-badge-sandbox" : "k-badge-accent"}`} data-testid="billing-balance-badge">
            <Sparkles size={14} /> {bal ?? "…"} credits
          </span>
          {low && <span className="k-muted" data-testid="billing-low-nudge" style={{ fontSize: 13 }}>
            You are below your low-balance threshold. Top up whenever it suits you.</span>}
        </div>
        {runway && (
          <div data-testid="runway-estimate" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <Gauge size={16} color="var(--accent-primary)" />
            <div>
              <div className="k-heading" style={{ fontSize: 15 }}>
                {runway.runway_days != null ? `~${runway.runway_days} days of runway` : "Runway: not enough usage yet"}
              </div>
              <div className="k-muted" style={{ fontSize: 12 }}>
                {runway.daily_burn_credits > 0
                  ? `Based on ~${runway.daily_burn_credits} credits/day over your last ${runway.days_of_data} days of activity.`
                  : runway.note}
              </div>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
          {(tiers.length ? tiers : [{ label: "Standard", credits_granted: 1000 }]).map((t, i) => (
            <button key={i} className="k-btn k-btn-primary" data-testid={`topup-${t.credits_granted}`}
              disabled={busy} onClick={() => topup(t.credits_granted)}>
              <Plus size={14} /> {t.label} · {t.credits_granted} credits
              {t.price_minor_units != null && <span style={{ marginLeft: 6, opacity: .75 }}>{money(t.price_minor_units)}</span>}
            </button>
          ))}
        </div>
        {!isGbp && (
          <div data-testid="billing-currency-note" className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>
            Top-ups are charged in GBP. In {curActive.code}, the tiers are approximately{" "}
            {(tiers.length ? tiers : []).filter(t => t.price_minor_units != null)
              .map(t => fmtCur(t.price_minor_units / 100)).join(" · ")} at recent mid-market rates.
          </div>
        )}
        {org && (
          <div style={{ marginTop: 12 }} data-testid="paypal-section">
            <PayPalTopUp orgId={org.id} credits={(tiers[0] && tiers[0].credits_granted) || 100} onGranted={load} />
          </div>
        )}
        {org && (
          <div style={{ marginTop: 12 }} data-testid="paystack-section">
            <PaystackTopUp orgId={org.id} credits={(tiers[0] && tiers[0].credits_granted) || 100} />
          </div>
        )}
        {org && (
          <div data-testid="mpesa-section">
            <MpesaTopUp orgId={org.id} credits={(tiers[0] && tiers[0].credits_granted) || 100} />
          </div>
        )}
      </div>

      {/* Item 8 — purchased vs promotional split (never merged) */}
      {breakdown && (
        <div className="k-container" data-testid="credit-breakdown" style={card}>
          <h3 className="k-heading" style={{ marginTop: 0, fontSize: 18 }}>Where your credits came from</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
            <div className="k-control" data-testid="breakdown-purchased" style={{ padding: 14 }}>
              <div className="k-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}><ShoppingCart size={13} /> Purchased</div>
              <div className="k-heading" style={{ fontSize: 24, fontWeight: 700 }}>{breakdown.purchased_credits}</div>
              <div className="k-muted" style={{ fontSize: 11 }}>Credits you paid for</div>
            </div>
            <div className="k-control" data-testid="breakdown-promotional" style={{ padding: 14 }}>
              <div className="k-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}><Gift size={13} /> Promotional / referral</div>
              <div className="k-heading" style={{ fontSize: 24, fontWeight: 700 }}>{breakdown.promotional_credits}</div>
              <div className="k-muted" style={{ fontSize: 11 }}>Free credits (sign-up, referrals, goodwill)</div>
            </div>
            <div className="k-control" style={{ padding: 14 }}>
              <div className="k-muted" style={{ fontSize: 12 }}>Balance now</div>
              <div className="k-heading" style={{ fontSize: 24, fontWeight: 700 }}>{breakdown.balance_credits}</div>
              <div className="k-muted" style={{ fontSize: 11 }}>{breakdown.total_spent} spent to date</div>
            </div>
          </div>
          <p className="k-muted" style={{ fontSize: 12, marginTop: 10 }}>{breakdown.note}</p>
        </div>
      )}

      {/* Item 5 — per-project credit breakdown (billing clarity) */}
      <div className="k-container" data-testid="per-app-breakdown" style={card}>
        <h3 className="k-heading" style={{ marginTop: 0, fontSize: 18 }}>Credit spend by app</h3>
        {perApp.length === 0 && <p className="k-muted" style={{ fontSize: 13 }}>No app spend recorded yet.</p>}
        {perApp.length > 0 && (
          <div className="k-table-wrap"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr className="k-muted" style={{ textAlign: "left" }}>
              <th style={{ padding: "8px 0" }}>App</th>
              <th style={{ textAlign: "right" }}>Credits used</th>
              <th style={{ textAlign: "right" }}>≈ Cost</th>
            </tr></thead>
            <tbody>
              {perApp.map(a => (
                <tr key={a.id} data-testid={`per-app-row-${a.id}`} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "8px 0" }}>{a.name}</td>
                  <td style={{ textAlign: "right" }}>{a.credits_used}</td>
                  <td style={{ textAlign: "right" }} className="k-muted">{money(a.credits_used * creditPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Item 4 + 10 — auto-reload and self-set spending limit */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16, marginTop: 18 }}>
        <div className="k-container" data-testid="auto-reload" style={{ padding: 22 }}>
          <h3 className="k-heading" style={{ marginTop: 0, fontSize: 17, display: "flex", alignItems: "center", gap: 8 }}>
            <RefreshCw size={16} /> Auto-reload <span className="k-badge k-badge-sandbox" style={{ fontSize: 10 }}>SANDBOX</span>
          </h3>
          <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
            <label className="k-muted" style={{ fontSize: 12 }}>Top up when balance falls below (credits)
              <input className="k-control" data-testid="ar-threshold" type="number" min={1} value={ar.threshold_credits}
                onChange={e => setAr(a => ({ ...a, threshold_credits: e.target.value }))} style={{ marginTop: 4 }} /></label>
            <label className="k-muted" style={{ fontSize: 12 }}>Add this many credits each time
              <input className="k-control" data-testid="ar-topup" type="number" min={1} value={ar.topup_credits}
                onChange={e => setAr(a => ({ ...a, topup_credits: e.target.value }))} style={{ marginTop: 4 }} /></label>
            <label className="k-muted" style={{ fontSize: 12 }}>Never spend more than this per month (£) — required
              <input className="k-control" data-testid="ar-cap" type="number" min={1} value={ar.monthly_cap_pounds}
                onChange={e => setAr(a => ({ ...a, monthly_cap_pounds: e.target.value }))} style={{ marginTop: 4 }} /></label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            {ar.enabled
              ? <button className="k-btn k-btn-ghost" data-testid="ar-disable" onClick={() => saveAutoReload(false)}>Turn off</button>
              : <button className="k-btn k-btn-primary" data-testid="ar-enable" disabled={!canEnableAr} onClick={() => saveAutoReload(true)}>Turn on auto-reload</button>}
            {ar.enabled && <span className="k-badge k-badge-accent" data-testid="ar-status"><ShieldCheck size={12} /> On · cap {money((ar.monthly_cap_pounds || 0) * 100)}/mo</span>}
          </div>
          {!canEnableAr && !ar.enabled && <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }} data-testid="ar-cap-required">Set a threshold, reload amount and a monthly cap above to enable.</p>}
        </div>

        <div className="k-container" data-testid="spending-limit" style={{ padding: 22 }}>
          <h3 className="k-heading" style={{ marginTop: 0, fontSize: 17, display: "flex", alignItems: "center", gap: 8 }}>
            <PiggyBank size={16} /> Your monthly spending limit
          </h3>
          <label className="k-muted" style={{ fontSize: 12 }}>Monthly limit (£) — leave blank for no limit
            <input className="k-control" data-testid="limit-input" type="number" min={0} value={limitPounds}
              onChange={e => setLimitPounds(e.target.value)} placeholder="e.g. 500" style={{ marginTop: 4 }} /></label>
          <button className="k-btn k-btn-primary" data-testid="limit-save" style={{ marginTop: 12 }} onClick={saveLimit}>Save limit</button>
          {settings?.spending_limit_minor != null &&
            <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }} data-testid="limit-current">Current limit: {money(settings.spending_limit_minor)}/month</p>}
        </div>
      </div>

      {/* Item 7 — saved payment methods with a clear default */}
      <div className="k-container" data-testid="payment-methods" style={card}>
        <h3 className="k-heading" style={{ marginTop: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
          <CreditCard size={16} /> Saved payment methods <span className="k-badge k-badge-sandbox" style={{ fontSize: 10 }}>SANDBOX</span>
        </h3>
        <div style={{ display: "grid", gap: 8 }}>
          {methods.map(p => (
            <div key={p.id} data-testid={`pm-${p.id}`} className="k-control" style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <CreditCard size={16} />
              <span>{p.brand} •••• {p.last4}</span>
              <span className="k-muted" style={{ fontSize: 12 }}>exp {String(p.exp_month).padStart(2, "0")}/{p.exp_year}{p.label ? ` · ${p.label}` : ""}</span>
              {p.is_default
                ? <span className="k-badge k-badge-accent" data-testid={`pm-default-${p.id}`} style={{ marginLeft: "auto" }}><Star size={12} /> Default</span>
                : <button className="k-btn k-btn-ghost" data-testid={`pm-make-default-${p.id}`} style={{ marginLeft: "auto" }} onClick={() => setDefaultPm(p.id)}>Set default</button>}
              <button className="k-btn k-btn-ghost" data-testid={`pm-remove-${p.id}`} onClick={() => removePm(p.id)}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
          <label className="k-muted" style={{ fontSize: 12 }}>Brand
            <input className="k-control" data-testid="pm-brand" value={pm.brand} onChange={e => setPm(v => ({ ...v, brand: e.target.value }))} style={{ marginTop: 4, width: 110 }} /></label>
          <label className="k-muted" style={{ fontSize: 12 }}>Last 4
            <input className="k-control" data-testid="pm-last4" maxLength={4} value={pm.last4} onChange={e => setPm(v => ({ ...v, last4: e.target.value }))} placeholder="4242" style={{ marginTop: 4, width: 90 }} /></label>
          <label className="k-muted" style={{ fontSize: 12 }}>Exp M
            <input className="k-control" data-testid="pm-exp-month" type="number" min={1} max={12} value={pm.exp_month} onChange={e => setPm(v => ({ ...v, exp_month: Number(e.target.value) }))} style={{ marginTop: 4, width: 70 }} /></label>
          <label className="k-muted" style={{ fontSize: 12 }}>Exp Y
            <input className="k-control" data-testid="pm-exp-year" type="number" min={2026} value={pm.exp_year} onChange={e => setPm(v => ({ ...v, exp_year: Number(e.target.value) }))} style={{ marginTop: 4, width: 90 }} /></label>
          <button className="k-btn k-btn-primary" data-testid="pm-add" onClick={addPm}><Plus size={13} /> Add method</button>
        </div>
        <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>Test key is active, so no real card is stored or charged. Live card capture uses Stripe when a real key is configured.</p>
      </div>

      {/* Item 9 — business invoicing details */}
      <div className="k-container" data-testid="invoicing-details" style={card}>
        <h3 className="k-heading" style={{ marginTop: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
          <Building2 size={16} /> Business invoicing details
        </h3>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
          <label className="k-muted" style={{ fontSize: 12 }}>Company name
            <input className="k-control" data-testid="inv-company" value={inv.company_name} onChange={e => setInv(v => ({ ...v, company_name: e.target.value }))} style={{ marginTop: 4 }} /></label>
          <label className="k-muted" style={{ fontSize: 12 }}>VAT number
            <input className="k-control" data-testid="inv-vat" value={inv.vat_number} onChange={e => setInv(v => ({ ...v, vat_number: e.target.value }))} placeholder="GB123456789" style={{ marginTop: 4 }} /></label>
        </div>
        <label className="k-muted" style={{ fontSize: 12, display: "block", marginTop: 10 }}>Billing address
          <textarea className="k-control" data-testid="inv-address" rows={2} value={inv.address} onChange={e => setInv(v => ({ ...v, address: e.target.value }))} style={{ marginTop: 4 }} /></label>
        <button className="k-btn k-btn-primary" data-testid="inv-save" style={{ marginTop: 12 }} onClick={saveInvoicing}>Save invoicing details</button>
      </div>
    </Shell>
  );
}
